import { useEffect, useState } from 'react';
import type {
  PublicStatusNamespaceRecord,
  PublicStatusResponse,
} from '@nadoutong/contracts';
import { PLATFORM_BASE_URL } from '../config/endpoints.ts';

/**
 * Epic #898 P6 (#918): the status page renders the namespace rollup
 * (`namespaces[]`, additive on `PublicStatusResponse` since #906)
 * instead of the channel-era vendor/channel records. `namespaces` is
 * OPTIONAL on the wire — an old deployed platform-api omits it — so the
 * hook normalizes the absent field to `[]` here and the page falls back
 * to a graceful empty state.
 */

interface StatusState {
  readonly data: PublicStatusResponse | null;
  /** `[]` while loading, on error, and on pre-#906 payloads. */
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
  readonly loading: boolean;
  readonly error: boolean;
}

export function useStatus(): StatusState {
  const [state, setState] = useState<StatusState>({
    data: null,
    namespaces: [],
    loading: true,
    error: false,
  });

  useEffect(() => {
    fetch(`${PLATFORM_BASE_URL}/v1/status?brand=nadoutong`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        const json = (await res.json()) as PublicStatusResponse;
        setState({
          data: json,
          namespaces: json.namespaces ?? [],
          loading: false,
          error: false,
        });
      })
      .catch(() => {
        setState({ data: null, namespaces: [], loading: false, error: true });
      });
  }, []);

  return state;
}
